import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPaymentIntent, verifyPayment, sendBookingEmail } from '../../api/payment';
import Swal from 'sweetalert2';
import '../../styles/payment.css';
import { AuthContext } from '../../context/AuthContext';

// Dynamically load Razorpay script
function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PaymentForm() {
  const { bookingId } = useParams();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  // Debug bookingId
  useEffect(() => {
    //console.log('📦 Booking ID from URL:', bookingId);
  }, [bookingId]);

  // Initialize payment intent
  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return;
    }

    createPaymentIntent(bookingId)
      .then((res) => {
        if (res.data.paymentStatus === 'paid') {
          setOrder({ paymentStatus: 'paid' });
        } else {
          setOrder(res.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        Swal.fire({
          icon: 'error',
          title: 'Payment Initialization Failed',
          text: err.response?.data?.message || 'Unable to create payment. Please try again later.',
          timer: 2500,
          showConfirmButton: false,
        });
        setLoading(false);
      });
  }, [bookingId]);

  // Redirect if already paid
  useEffect(() => {
    if (order?.paymentStatus === 'paid') {
      Swal.fire({
        icon: 'info',
        title: 'Already Paid',
        text: 'This booking has already been paid',
        timer: 2000,
        showConfirmButton: false,
      }).then(() => navigate('/bookings'));
    }
  }, [order, navigate]);

  // Handle case where no order exists after loading
  useEffect(() => {
    if (!loading && !order) {
      Swal.fire({
        icon: 'error',
        title: 'No Order Found',
        text: 'Unable to proceed with payment. Please try again.',
      }).then(() => navigate('/'));
    }
  }, [loading, order, navigate]);

  const handlePay = async () => {
    if (!order || !order.orderId || !order.amount) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Payment Data',
        text: 'Cannot start payment. Please refresh and try again.',
      });
      return;
    }

    const loaded = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    if (!loaded) {
      Swal.fire({
        icon: 'error',
        title: 'Razorpay SDK Load Failed',
        text: 'Please check your internet connection and try again.',
      });
      return;
    }

    const { orderId, amount } = order;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: amount * 100,
      currency: 'INR',
      name: 'Onenessence',
      order_id: orderId,
      handler: async (res) => {
        setIsProcessing(true);

        try {
          // Verify payment
          await verifyPayment({ ...res, bookingId });

          Swal.fire({
            icon: 'success',
            title: 'Payment Verified ✅',
            text: 'Your payment has been successfully verified.',
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (err) {
          setIsProcessing(false);
          return Swal.fire({
            icon: 'error',
            title: 'Verification Failed',
            text: err.response?.data?.message || 'Payment verification failed. Please contact support.',
          });
        }

        try {
          // Send booking confirmation email
          await sendBookingEmail(bookingId);

          Swal.fire({
            icon: 'success',
            title: 'Confirmation Email Sent 📧',
            text: 'Check your inbox for the booking confirmation.',
            timer: 1500,
            showConfirmButton: false,
          }).then(() => navigate('/bookings'));
        } catch (emailErr) {
          setIsProcessing(false);
          Swal.fire({
            icon: 'warning',
            title: 'Payment Success but Email Failed',
            text: emailErr.response?.data?.message || 'We could not send the email. Please contact support if needed.',
          }).then(() => navigate('/bookings'));
        }
      },
      prefill: {
        email: user?.email,
      },
      theme: {
        color: '#673AB7',
      },
    };

    new window.Razorpay(options).open();
  };

  if (loading) return <p className="payment-container">Loading payment details...</p>;
  if (!order) return null;

  return (
    <div className="payment-container">
      {isProcessing ? (
        <p>Processing payment, please wait...</p>
      ) : (
        <>
          <h2>Pay ₹{order.amount}</h2>
          <button onClick={handlePay}>Pay Now</button>
        </>
      )}
    </div>
  );
}
